import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSandboxSession } from '../SandboxSessionContext';
import { useBranchActionTarget } from './useBranchActionTarget';
import { useBranchReview } from './useBranchReview';

/** The overlay owns one inspected revision for pages, Changes and publication. */
export const useSandboxPreviewBannerController = () => {
  const session = useSandboxSession();
  const actionTarget = useBranchActionTarget();
  const location = useLocation();
  const navigate = useNavigate();
  const pinnedId = session.previewSandboxHints?.active_overlay_id || null;
  const approvalId = session.previewSandboxHints?.approval_id || null;
  const state = useBranchReview({
    branchId: session.previewSandboxId,
    revisionId: pinnedId || session.sandboxContext?.active_overlay_id || null,
    approvalId,
  });
  const { review, context, query, artifactsQuery } = state;
  const error =
    query.error ||
    artifactsQuery.error ||
    (!query.isLoading && state.changes && !review
      ? new Error('Draft details could not be loaded. Your saved changes are still available.')
      : null);
  const snapshot = review?.sandbox_context?.workspace_snapshot_key;
  useEffect(() => {
    if (error || !context.revisionId) return;
    if (
      pinnedId === context.revisionId &&
      (!snapshot || session.previewSandboxHints?.workspace_snapshot_key === snapshot)
    )
      return;
    session.pinPreviewRevision(context.branchId, {
      ...review?.sandbox_context,
      active_overlay_id: context.revisionId,
      approval_id: approvalId,
    });
  }, [
    error,
    context.branchId,
    context.revisionId,
    pinnedId,
    snapshot,
    review,
    approvalId,
    session.pinPreviewRevision,
    session.previewSandboxHints?.workspace_snapshot_key,
  ]);
  const ready = Boolean(
    review &&
      context.revisionId &&
      pinnedId === context.revisionId &&
      (!snapshot || session.previewSandboxHints?.workspace_snapshot_key === snapshot) &&
      !error &&
      !query.isLoading &&
      !artifactsQuery.isLoading
  );
  const sandbox = state.branch || session.selectedSandbox || { sandbox_id: session.previewSandboxId };
  const openLatest = () => {
    const revisionId =
      state.latestRevision ||
      session.selectedSandbox?.head_revision_id ||
      review?.head_revision_id ||
      session.selectedSandbox?.active_overlay_id;
    const params = new URLSearchParams(location.search);
    params.set('sandbox', session.previewSandboxId);
    params.delete('sandbox_id');
    params.delete('revision');
    params.delete('approval');
    params.delete('artifact');
    if (revisionId) params.set('overlay', revisionId);
    else params.delete('overlay');
    navigate(
      {
        pathname: location.pathname,
        search: '?' + params,
        hash: location.hash,
      },
      { replace: true }
    );
  };
  return {
    ...session,
    reviewState: {
      ...state, ready, error, openLatest,
      loadingLabel: query.isLoading || !review
        ? 'Loading branch revision…'
        : artifactsQuery.isLoading
          ? 'Loading branch definitions…'
          : 'Preparing preview…',
    },
    previewBannerSandbox: sandbox,
    previewBannerProps: {
      sandbox,
      revisionNumber: review?.revision_number,
      isCommitted: review?.is_committed,
      hasNewerRevision: state.hasNewerRevision,
      onRefresh: openLatest,
      onToggleChanges: session.toggleChangesPanel,
      showChanges: session.showChangesPanel,
      onExitPreview: session.clearPreview,
      actsOnBranch: Boolean(actionTarget && !actionTarget.unavailableReason),
    },
  };
};
export default useSandboxPreviewBannerController;
